import { useEffect } from 'react';
import { FilterOptions_AppointmentQualifier as AppointmentQualifier } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/bulk.pb';
import dayjs from 'dayjs';
import { getTodaysDate } from '@frontend/date';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DateRangeField, DropdownField, Text, useFormField } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { DateRange } from './types';
import { formatRange } from './utils/format-range';
import { rangeHasDate } from './utils/range-has-date';

type Props = {
  appointmentDateRange: DateRange | undefined;
  appointmentQualifier: AppointmentQualifier | undefined;
  onChangeAppointmentDateRange: (appointmentDateRange: DateRange | undefined) => void;
  onChangeAppointmentQualifier: (appointmentQualifier: AppointmentQualifier) => void;
  resetKey: number;
};

export const AppointmentDateFilter = ({
  appointmentDateRange,
  appointmentQualifier,
  onChangeAppointmentDateRange,
  onChangeAppointmentQualifier,
  resetKey,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { trackingIdPrefix } = useAudienceTray();

  const appointmentQualifierField = useFormField(
    {
      type: 'dropdown',
      value: appointmentQualifier,
    },
    [resetKey]
  );

  const appointmentQualifierOptions = {
    [AppointmentQualifier.NEXT_APPOINTMENT]: t('Next Appointment'),
    [AppointmentQualifier.LAST_SEEN]: t('Last Seen'),
    [AppointmentQualifier.NO_APPOINTMENT]: t('No Appointment'),
  };

  const appointmentDateField = useFormField(
    {
      type: 'dateRange',
      value: formatRange(appointmentDateRange, 'MM/DD/YYYY'),
      required: true,
      ...createMinMax(appointmentQualifier), // TODO: createMinMax properly creates the min/max dates based on the appointmentQualifier, but it is not updated in the component when it changes. Figure out why and fix it.
      validator: (dateRangeField) => {
        const value = dateRangeField.value;

        if (!value[0] || !value[1]) {
          return '';
        }

        if (dayjs(value[0]).isBefore(dayjs(dateRangeField.minDate))) {
          return t('Date range must be in the future');
        }

        if (dayjs(value[1]).isAfter(dayjs(dateRangeField.maxDate))) {
          return t('Date range must be in the past');
        }

        return '';
      },
    },
    [resetKey]
  );

  // This useEffect is needed to ensure onChangeAppointmentDateRange is passed the complete DateRange (instead of the partial DateRange the user just entered)
  useEffect(() => {
    if (appointmentDateField.value !== appointmentDateRange) {
      const range = rangeHasDate(appointmentDateField.value) ? appointmentDateField.value : undefined;
      onChangeAppointmentDateRange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDateField.value]); // TODO: Exhaustively listing all dependencies will cause an infinite loop. Fixing this requires a larger architectural change.

  return (
    <>
      <Text size='small'>{t('Appointment Date')}</Text>
      <DropdownField
        {...appointmentQualifierField}
        label={appointmentQualifierField.value === '' ? t('All') : ''}
        name='appointmentQualifier'
        data-trackingid={`${trackingIdPrefix}-appointment-date-type-input`}
        onChange={(e) => {
          appointmentQualifierField.onChange(e);
          if (e?.value !== appointmentQualifier) {
            onChangeAppointmentQualifier(e?.value as AppointmentQualifier);
          }
        }}
      >
        <DropdownField.Option value={''}>{t('All Appointments')}</DropdownField.Option>
        {Object.entries(appointmentQualifierOptions).map(([key, label]) => (
          <DropdownField.Option key={key} value={key}>
            {label}
          </DropdownField.Option>
        ))}
      </DropdownField>
      <DateRangeField
        {...appointmentDateField}
        label=''
        name='appointmentDate'
        onChange={(e) => {
          appointmentDateField.onChange(e);
          // @ts-expect-error - Types for form field events are not accurate
          const updatedValue = e?.value;
          if (updatedValue === appointmentDateRange) return;
          const range = rangeHasDate(updatedValue) ? updatedValue : undefined;
          onChangeAppointmentDateRange(range);
        }}
      />
      {appointmentDateField.error && (
        <Text
          size='small'
          color='error'
          css={{
            marginTop: theme.spacing(0.5),
          }}
        >
          {appointmentDateField.error}
        </Text>
      )}
      <Divider margin={theme.spacing(1)} />
    </>
  );
};

const createMinMax = (appointmentQualifier?: AppointmentQualifier) => {
  switch (appointmentQualifier) {
    case AppointmentQualifier.NEXT_APPOINTMENT:
      return {
        maxDate: '',
        minDate: dayjs().add(1, 'day').format('MM/DD/YYYY'),
      };

    case AppointmentQualifier.LAST_SEEN:
      return {
        maxDate: getTodaysDate('MM/DD/YYYY'),
        minDate: '',
      };

    default: // 'No Appointment' or 'All'
      return {
        maxDate: '',
        minDate: '',
      };
  }
};
